import { useState, useEffect } from "react";
import { apiGet } from "../api/client";
import { PageHeader } from "../components/ui/Primitives";
import { fmtKst } from "../lib/format";
import { MetaLabelerInfo, CandidateResponse } from "../api/types";
import {
  Brain,
  RefreshCw,
  Cpu,
  Layers,
  Sparkles,
  Zap,
} from "lucide-react";

interface ModelRecord {
  model_id: string;
  kind: string;
  status: string;
  trained_at: string | null;
  feature_set_hash: string | null;
  artifact_path: string | null;
  metrics: Record<string, any> | null;
}

export default function Models() {
  const [metaInfo, setMetaInfo] = useState<MetaLabelerInfo | null>(null);
  const [candidateData, setCandidateData] = useState<CandidateResponse | null>(null);
  const [models, setModels] = useState<ModelRecord[]>([]);
  const [selectedModel, setSelectedModel] = useState<ModelRecord | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [metaRes, candRes, modRes] = await Promise.all([
        apiGet<MetaLabelerInfo>("/models/meta-labeler").catch(() => null),
        apiGet<CandidateResponse>("/models/candidates?limit=20").catch(() => null),
        apiGet<{ models: ModelRecord[]; count: number }>("/models").catch(() => ({ models: [], count: 0 })),
      ]);
      setMetaInfo(metaRes);
      setCandidateData(candRes);
      setModels(modRes.models || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "ELIGIBLE":
        return "운영 배포 (Eligible)";
      case "SHADOW":
        return "그림자 평가 (Shadow)";
      case "RETIRED":
        return "퇴역 (Retired)";
      default:
        return status;
    }
  };

  const formatProb = (val: number | null | undefined) => {
    if (val === null || val === undefined || isNaN(Number(val))) return "—";
    return `${(Number(val) * 100).toFixed(1)}%`;
  };

  return (
    <div>
      <PageHeader
        icon={<Brain size={24} color="var(--accent)" />}
        title="AI & 예측 모델 허브"
        description="단일 2차 필터 신경망(MetaMLP), 방향 대칭 연산 및 72h Candidate 수집 현황"
        actions={
          <button className="btn-secondary" onClick={fetchAll} disabled={loading}>
            <RefreshCw size={14} className={loading ? "spin" : ""} /> 새로고침
          </button>
        }
      />

      {/* 1. PyTorch Meta-Labeler Card */}
      <div className="card" style={{ marginBottom: "var(--space-4)" }}>
        <div className="card-title-row">
          <Zap size={16} color="var(--accent)" />
          <h3 className="card-title">PyTorch Meta-Labeler (`MetaMLP`)</h3>
          <span
            className={`badge ${metaInfo?.is_active ? "badge-green" : "badge-yellow"}`}
            style={{ marginLeft: "auto", padding: "4px 10px" }}
          >
            {metaInfo?.is_active
              ? metaInfo?.shadow_mode
                ? "Shadow Mode (0% Live Impact)"
                : "Active Production"
              : "Disabled / Shadow Collection"}
          </span>
        </div>

        <div className="grid grid-4" style={{ marginBottom: 16 }}>
          <div className="metric-tile">
            <div className="metric-tile-label">모델 아키텍처</div>
            <div className="metric-tile-value" style={{ fontSize: 16 }}>MetaMLP</div>
            <div className="metric-tile-sub">9 In → 16 (BN/Drop) → 8 → 1 Sigmoid</div>
          </div>
          <div className="metric-tile">
            <div className="metric-tile-label">파라미터 및 크기</div>
            <div className="metric-tile-value" style={{ fontSize: 16 }}>
              {metaInfo?.architecture.parameter_count ?? 313} params
            </div>
            <div className="metric-tile-sub">체크포인트 크기: 4.6 KB</div>
          </div>
          <div className="metric-tile">
            <div className="metric-tile-label">추론 지연 (Inference)</div>
            <div className="metric-tile-value success" style={{ fontSize: 16 }}>
              ~{metaInfo?.architecture.inference_latency_us ?? 30.0} µs
            </div>
            <div className="metric-tile-sub">초저지연 C++ 텐서 연산</div>
          </div>
          <div className="metric-tile">
            <div className="metric-tile-label">스키마 버전</div>
            <div className="metric-tile-value" style={{ fontSize: 16 }}>
              {metaInfo?.schema_version ?? "v2.0"}
            </div>
            <div className="metric-tile-sub">강타입 마이크로구조 스키마</div>
          </div>
        </div>

        {/* Directional Symmetry Logic Notice */}
        <div className="notice-card" style={{ fontSize: "var(--text-sm)", marginBottom: 12 }}>
          <strong>방향 대칭성 (Directional Symmetry) 연산: </strong>
          {metaInfo?.directional_symmetry.description ??
            "롱/숏 진입 신호 간의 구조적 비대칭 편향을 제거하기 위해 숏 평가 시 방향성 피처를 수학적으로 반전하여 단일 모델로 대칭 평가합니다."}
          <div style={{ marginTop: 6, fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)" }}>
            • 롱 진입: {metaInfo?.directional_symmetry.long_formula ?? "y_long = MetaMLP(x)"}
            <br />
            • 숏 진입: {metaInfo?.directional_symmetry.short_formula ?? "y_short = MetaMLP(invert_features(x))"}
          </div>
        </div>

        <div className="muted-note" style={{ fontSize: "var(--text-xs)" }}>
          {metaInfo?.evaluation_gates.live_impact_note ??
            "INV-V2-M1/M2/M4: 모델 평가 기준(n>=1000, PR-AUC>=0.60, Accuracy>=0.55) 충족 전까지 실거래 주문 크기 변경 0% (섀도우/비활성)"}
        </div>
      </div>

      {/* 2. Candidate Dataset Collection Gauge */}
      <div className="card" style={{ marginBottom: "var(--space-4)" }}>
        <div className="card-title-row">
          <Sparkles size={16} color="var(--purple)" />
          <h3 className="card-title">실시간 Candidate 데이터셋 수집 현황 (`meta_candidate`)</h3>
          <span className="card-title-meta">72시간 소크 동안 전략 진입 전수 수집 및 Triple-Barrier 레이블링</span>
        </div>

        {/* Progress Bar */}
        <div style={{ marginTop: 8, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "var(--text-sm)", marginBottom: 6, fontWeight: 600 }}>
            <span>
              학습 게이트 목표: <strong style={{ color: "var(--purple)" }}>{candidateData?.total_candidates ?? 0}건</strong> / {candidateData?.target_candidates ?? 1000}건
            </span>
            <span>수집 진척도: <strong>{candidateData?.collection_progress_pct ?? 0}%</strong></span>
          </div>
          <div style={{ height: 8, borderRadius: 4, background: "var(--surface-input)", overflow: "hidden" }}>
            <div
              style={{
                height: "100%",
                width: `${Math.min(100, Math.max(0.5, candidateData?.collection_progress_pct ?? 0.5))}%`,
                background: "var(--purple)",
                borderRadius: 4,
                transition: "width 0.4s ease",
              }}
            />
          </div>
        </div>

        <div className="grid grid-4" style={{ marginBottom: 16 }}>
          <div className="metric-tile">
            <div className="metric-tile-label">총 수집 후보 (Candidates)</div>
            <div className="metric-tile-value">{candidateData?.total_candidates ?? 0}건</div>
            <div className="metric-tile-sub">전략 1차 트리거 전수 기록</div>
          </div>
          <div className="metric-tile">
            <div className="metric-tile-label">레이블 해결 완료 (Resolved)</div>
            <div className="metric-tile-value">{candidateData?.labeled_candidates ?? 0}건</div>
            <div className="metric-tile-sub">Triple-Barrier 도달 완료</div>
          </div>
          <div className="metric-tile">
            <div className="metric-tile-label">비용 후 성공 (Label = 1)</div>
            <div className="metric-tile-value success">{candidateData?.positive_labels ?? 0}건</div>
            <div className="metric-tile-sub">수수료/슬리피지 공제 후 익절</div>
          </div>
          <div className="metric-tile">
            <div className="metric-tile-label">비용 후 손실 (Label = 0)</div>
            <div className="metric-tile-value danger">{candidateData?.negative_labels ?? 0}건</div>
            <div className="metric-tile-sub">스탑로스 또는 시간청산 손실</div>
          </div>
        </div>

        {/* Recent Candidates Table */}
        <div style={{ marginTop: 12 }}>
          <h4 style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 8 }}>
            최근 수집된 의사결정 후보 ({candidateData?.recent_candidates?.length ?? 0}건)
          </h4>
          {!candidateData?.recent_candidates || candidateData.recent_candidates.length === 0 ? (
            <div style={{ textAlign: "center", padding: "24px 16px", color: "var(--text-muted)", fontStyle: "italic", fontSize: "var(--text-sm)" }}>
              수집된 후보가 아직 없습니다 (소크 진입 시 실시간 누적 기록됨)
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>결정 일시</th>
                  <th>전략</th>
                  <th>심볼</th>
                  <th>방향</th>
                  <th>진입 기준가</th>
                  <th>손절가</th>
                  <th>익절가</th>
                  <th>예측 확률</th>
                  <th>레이블 결과</th>
                </tr>
              </thead>
              <tbody>
                {candidateData.recent_candidates.map((c) => (
                  <tr key={c.candidate_id}>
                    <td style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
                      {new Date(c.decision_ts).toLocaleTimeString("ko-KR")}
                    </td>
                    <td>
                      <span className="badge badge-purple" style={{ fontSize: "var(--text-xs)" }}>{c.strategy}</span>
                    </td>
                    <td style={{ fontWeight: 700 }}>{c.symbol}</td>
                    <td>
                      <span className={`badge ${c.side === "LONG" ? "badge-green" : "badge-red"}`}>
                        {c.side}
                      </span>
                    </td>
                    <td>${Number(c.entry_reference_px).toFixed(2)}</td>
                    <td style={{ color: "var(--red)" }}>${Number(c.stop_price).toFixed(2)}</td>
                    <td style={{ color: "var(--green)" }}>${Number(c.take_profit).toFixed(2)}</td>
                    <td style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)" }}>
                      {formatProb(c.predicted_probability)}
                    </td>
                    <td>
                      {c.label === 1 ? (
                        <span className="badge badge-green" style={{ fontSize: "var(--text-xs)" }}>성공 (Label 1)</span>
                      ) : c.label === 0 ? (
                        <span className="badge badge-red" style={{ fontSize: "var(--text-xs)" }}>손실 (Label 0)</span>
                      ) : (
                        <span className="badge badge-yellow" style={{ fontSize: "var(--text-xs)" }}>평가 진행 중</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* 3. 9 Microstructure Features Schema */}
      {metaInfo?.features && (
        <div className="card" style={{ marginBottom: "var(--space-4)" }}>
          <div className="card-title-row">
            <Layers size={16} color="var(--accent)" />
            <h3 className="card-title">9대 마이크로구조 피처 스키마 (Versioned Feature Contract)</h3>
            <span className="card-title-meta">스키마 버전 {metaInfo.schema_version}</span>
          </div>
          <table>
            <thead>
              <tr>
                <th>피처 키 (Feature Key)</th>
                <th>단위 (Unit)</th>
                <th>결측 대체 (Policy)</th>
                <th>중립값</th>
                <th>클리핑 범위 (Clip Min ~ Max)</th>
                <th>설명 (Description)</th>
              </tr>
            </thead>
            <tbody>
              {metaInfo.features.map((f) => (
                <tr key={f.key}>
                  <td style={{ fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: "var(--text-sm)", color: "var(--text-primary)" }}>
                    {f.key}
                  </td>
                  <td>
                    <span className="badge badge-blue" style={{ fontSize: "var(--text-xs)" }}>{f.unit}</span>
                  </td>
                  <td style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>{f.missing_policy}</td>
                  <td style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)" }}>{f.neutral_value}</td>
                  <td style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)" }}>
                    [{f.clip_min ?? "-∞"}, {f.clip_max ?? "+∞"}]
                  </td>
                  <td style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>{f.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 4. Historical Model Registry (Collapsible/Secondary) */}
      <div className="card">
        <div className="card-title-row">
          <Cpu size={16} color="var(--text-muted)" />
          <h3 className="card-title">등록된 AI 모델 레지스트리 ({models.length}개)</h3>
        </div>
        {models.length === 0 ? (
          <div style={{ color: "var(--text-muted)", padding: "20px", textAlign: "center", fontStyle: "italic", fontSize: "var(--text-sm)" }}>
            등록된 레거시 AI 모델이 없습니다 (KORU-v2 PyTorch Meta-Labeler 체제로 단일화됨)
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>모델 ID</th>
                <th>유형 (Kind)</th>
                <th>운영 상태</th>
                <th>학습 일시</th>
              </tr>
            </thead>
            <tbody>
              {models.map((m) => (
                <tr
                  key={m.model_id}
                  onClick={() => setSelectedModel(m)}
                  style={{
                    cursor: "pointer",
                    background: selectedModel?.model_id === m.model_id ? "var(--bg-hover)" : undefined,
                  }}
                >
                  <td style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
                    {m.model_id?.slice(0, 16)}
                  </td>
                  <td style={{ fontWeight: 700 }}>{m.kind}</td>
                  <td>
                    <span
                      className={`badge ${
                        m.status === "ELIGIBLE"
                          ? "badge-green"
                          : m.status === "SHADOW"
                          ? "badge-blue"
                          : m.status === "RETIRED"
                          ? "badge-red"
                          : "badge-yellow"
                      }`}
                    >
                      {getStatusLabel(m.status)}
                    </span>
                  </td>
                  <td style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
                    {m.trained_at ? fmtKst(m.trained_at).slice(0, 10) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {selectedModel && (
          <div style={{ marginTop: "var(--space-4)", borderTop: "1px solid var(--border)", paddingTop: 12 }}>
            <h4 style={{ fontSize: 13, marginBottom: 8, color: "var(--text-secondary)" }}>선택된 모델 메트릭 ({selectedModel.model_id})</h4>
            <pre className="code-block" style={{ maxHeight: 200, fontSize: "var(--text-xs)" }}>
              {JSON.stringify(selectedModel.metrics, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}
